import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useAnalytics } from './AnalyticsContext';
import { v4 as uuidv4 } from 'uuid';

const EnhancedAnalyticsContext = createContext();

export const useEnhancedAnalytics = () => {
  const context = useContext(EnhancedAnalyticsContext);
  if (!context) {
    throw new Error('useEnhancedAnalytics must be used within an EnhancedAnalyticsProvider');
  }
  return context;
};

export const EnhancedAnalyticsProvider = ({ children }) => {
  const { user } = useAuth();
  const { config, consentGiven } = useAnalytics();
  
  // Session and tracking state
  const [sessionId] = useState(() => {
    // Try to get existing session ID from sessionStorage
    let existingSessionId = sessionStorage.getItem('analytics_session_id');
    if (!existingSessionId) {
      existingSessionId = uuidv4();
      sessionStorage.setItem('analytics_session_id', existingSessionId);
    }
    return existingSessionId;
  });
  
  const [clientId] = useState(() => {
    // Try to get existing client ID from localStorage
    let existingClientId = localStorage.getItem('ga_client_id');
    if (!existingClientId) {
      existingClientId = uuidv4();
      localStorage.setItem('ga_client_id', existingClientId);
    }
    return existingClientId;
  });

  // Tracking state
  const [pageStartTime, setPageStartTime] = useState(Date.now());
  const [scrollDepth, setScrollDepth] = useState(0);
  const [maxScrollDepth, setMaxScrollDepth] = useState(0);
  const [eventQueue, setEventQueue] = useState([]);
  const [isTracking, setIsTracking] = useState(false);
  const [sessionStartTracked, setSessionStartTracked] = useState(false);

  // Refs for tracking
  const pageViewTimer = useRef(null);
  const scrollTimer = useRef(null);
  const eventQueueTimer = useRef(null);
  const maxScrollDepthRef = useRef(0);
  const scrollMilestonesTracked = useRef(new Set());

  // Get screen and viewport info
  const getScreenInfo = useCallback(() => {
    return {
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`
    };
  }, []);

  // Calculate scroll depth
  const calculateScrollDepth = useCallback(() => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );
    
    const scrollPercent = Math.round((scrollTop + windowHeight) / documentHeight * 100);
    return Math.min(scrollPercent, 100);
  }, []);

  // Enhanced event tracking (defined early so other callbacks can use it)
  const trackEvent = useCallback(async (eventType, eventData = {}) => {
    if (!consentGiven || !config?.analytics_enabled) {
      return;
    }

    try {
      const timeOnPage = Math.round((Date.now() - pageStartTime) / 1000);
      
      const enhancedEventData = {
        sessionId,
        clientId,
        eventType,
        pageUrl: window.location.href,
        pageTitle: document.title,
        referrer: document.referrer,
        timeOnPage: timeOnPage > 0 ? timeOnPage : null,
        scrollDepth: maxScrollDepth > 0 ? maxScrollDepth : null,
        ...getScreenInfo(),
        eventData: {
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          language: navigator.language,
          ...eventData
        }
      };

      // Add to queue for batch processing
      setEventQueue(prev => [...prev, enhancedEventData]);

    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }, [consentGiven, config, sessionId, clientId, pageStartTime, maxScrollDepth, getScreenInfo]);

  // Track scroll depth (defined after trackEvent)
  const handleScroll = useCallback(() => {
    if (scrollTimer.current) {
      clearTimeout(scrollTimer.current);
    }

    scrollTimer.current = setTimeout(() => {
      const currentScrollDepth = calculateScrollDepth();
      setScrollDepth(currentScrollDepth);
      
      if (currentScrollDepth > maxScrollDepthRef.current) {
        maxScrollDepthRef.current = currentScrollDepth;
        setMaxScrollDepth(currentScrollDepth);
        
        // Track milestone scroll depths (only once per milestone per page)
        const milestones = [25, 50, 75, 90, 100];
        const newMilestones = milestones.filter(m => 
          currentScrollDepth >= m && !scrollMilestonesTracked.current.has(m)
        );
        
        newMilestones.forEach(milestone => {
          scrollMilestonesTracked.current.add(milestone);
          trackEvent('scroll', {
            scroll_depth: milestone,
            page_url: window.location.href,
            page_title: document.title
          });
        });
      }
    }, 100);
  }, [calculateScrollDepth, trackEvent]);

  // Track page view with enhanced data (defined after trackEvent)
  const trackPageView = useCallback((additionalData = {}) => {
    if (!consentGiven || !config?.analytics_enabled) return;

    const pageData = {
      eventType: 'page_view',
      pageUrl: window.location.href,
      pageTitle: document.title,
      referrer: document.referrer,
      ...getScreenInfo(),
      eventData: {
        utm_source: new URLSearchParams(window.location.search).get('utm_source'),
        utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
        utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
        utm_content: new URLSearchParams(window.location.search).get('utm_content'),
        utm_term: new URLSearchParams(window.location.search).get('utm_term'),
        ...additionalData
      }
    };

    trackEvent('page_view', pageData);
    setPageStartTime(Date.now());
    setMaxScrollDepth(0);
    maxScrollDepthRef.current = 0;
    scrollMilestonesTracked.current.clear(); // Reset scroll milestones for new page
  }, [consentGiven, config, getScreenInfo, trackEvent]);

  // Process event queue
  const processEventQueue = useCallback(async () => {
    setEventQueue(currentQueue => {
      if (currentQueue.length === 0) {
        return currentQueue;
      }

      // Process events asynchronously
      (async () => {
        try {
          await axios.post('/api/user-journey/track-batch', {
            events: currentQueue
          });
        } catch (error) {
          console.error('Error processing event queue:', error);
          // Re-add failed events to queue (only if they failed)
          setEventQueue(prev => [...currentQueue, ...prev]);
        }
      })();

      // Clear the queue immediately
      return [];
    });
  }, []);

  // E-commerce tracking functions
  const trackProductView = useCallback((product) => {
    trackEvent('product_view', {
      product_id: product.id,
      product_name: product.name,
      product_category: product.category?.name,
      product_price: product.price_b2c || product.price,
      product_brand: product.brand,
      product_sku: product.sku,
      currency: 'ILS'
    });
  }, [trackEvent]);

  const trackCategoryView = useCallback((category, products = []) => {
    trackEvent('category_view', {
      category_id: category.id,
      category_name: category.name,
      products_count: products.length,
      products_shown: products.slice(0, 10).map(p => ({
        id: p.id,
        name: p.name,
        price: p.price_b2c || p.price
      }))
    });
  }, [trackEvent]);

  const trackProductListView = useCallback((listName, products = [], listId = null) => {
    trackEvent('product_list_view', {
      list_name: listName,
      list_id: listId,
      products_count: products.length,
      products: products.map((product, index) => ({
        id: product.id,
        name: product.name,
        category: product.category?.name,
        price: product.price_b2c || product.price,
        position: index + 1
      }))
    });
  }, [trackEvent]);

  const trackAddToCart = useCallback((product, quantity = 1) => {
    const value = (product.price_b2c || product.price || 0) * quantity;
    
    trackEvent('add_to_cart', {
      product_id: product.id,
      product_name: product.name,
      product_category: product.category?.name,
      quantity: quantity,
      price: product.price_b2c || product.price,
      value: value,
      currency: 'ILS',
      conversionValue: value
    });
  }, [trackEvent]);

  const trackRemoveFromCart = useCallback((product, quantity = 1) => {
    const value = (product.price_b2c || product.price || 0) * quantity;
    
    trackEvent('remove_from_cart', {
      product_id: product.id,
      product_name: product.name,
      quantity: quantity,
      price: product.price_b2c || product.price,
      value: value,
      currency: 'ILS'
    });
  }, [trackEvent]);

  const trackViewCart = useCallback((cartItems = []) => {
    const totalValue = cartItems.reduce((sum, item) => {
      return sum + (item.product?.price_b2c || item.product?.price || 0) * item.quantity;
    }, 0);

    trackEvent('view_cart', {
      cart_items_count: cartItems.length,
      cart_value: totalValue,
      currency: 'ILS',
      items: cartItems.map(item => ({
        product_id: item.product?.id,
        product_name: item.product?.name,
        quantity: item.quantity,
        price: item.product?.price_b2c || item.product?.price
      }))
    });
  }, [trackEvent]);

  const trackBeginCheckout = useCallback((cartItems = [], totalValue = 0) => {
    trackEvent('begin_checkout', {
      cart_items_count: cartItems.length,
      cart_value: totalValue,
      currency: 'ILS',
      conversionValue: totalValue,
      items: cartItems.map(item => ({
        product_id: item.product?.id,
        product_name: item.product?.name,
        quantity: item.quantity,
        price: item.product?.price_b2c || item.product?.price
      }))
    });
  }, [trackEvent]);

  const trackAddPaymentInfo = useCallback((paymentMethod, totalValue = 0) => {
    trackEvent('add_payment_info', {
      payment_method: paymentMethod,
      value: totalValue,
      currency: 'ILS'
    });
  }, [trackEvent]);

  const trackPurchase = useCallback((order) => {
    trackEvent('purchase', {
      transaction_id: order.order_number || order.id,
      value: order.total_amount,
      currency: order.currency || 'ILS',
      tax: order.tax_amount,
      shipping: order.shipping_amount,
      coupon: order.coupon_code,
      payment_method: order.payment_method,
      conversionValue: order.total_amount,
      items: (order.items || []).map(item => ({
        product_id: item.product?.id,
        product_name: item.product?.name,
        category: item.product?.category?.name,
        quantity: item.quantity,
        price: item.unit_price
      }))
    });
  }, [trackEvent]);

  const trackSearch = useCallback((searchTerm, resultsCount = 0, filters = {}) => {
    trackEvent('search', {
      search_term: searchTerm,
      results_count: resultsCount,
      filters_applied: Object.keys(filters).length > 0 ? filters : null
    });
  }, [trackEvent]);

  const trackWishlistAdd = useCallback((product) => {
    trackEvent('wishlist_add', {
      product_id: product.id,
      product_name: product.name,
      product_category: product.category?.name,
      product_price: product.price_b2c || product.price,
      currency: 'ILS'
    });
  }, [trackEvent]);

  const trackSocialShare = useCallback((content, platform) => {
    trackEvent('social_share', {
      content_type: content.type || 'product',
      content_id: content.id,
      content_name: content.name || content.title,
      platform: platform
    });
  }, [trackEvent]);

  const trackFormInteraction = useCallback((formName, action, fieldName = null) => {
    trackEvent('form_interaction', {
      form_name: formName,
      action: action, // 'start', 'complete', 'abandon', 'field_focus'
      field_name: fieldName
    });
  }, [trackEvent]);

  // Initialize tracking
  useEffect(() => {
    if (!consentGiven || !config?.analytics_enabled) return;

    setIsTracking(true);

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Process event queue periodically (every 5 seconds)
    eventQueueTimer.current = setInterval(() => {
      if (eventQueue.length > 0) {
        processEventQueue();
      }
    }, 5000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (pageViewTimer.current) clearTimeout(pageViewTimer.current);
      if (scrollTimer.current) clearTimeout(scrollTimer.current);
      if (eventQueueTimer.current) clearInterval(eventQueueTimer.current);
      
      // Process remaining events before unmount
      if (eventQueue.length > 0) {
        processEventQueue();
      }
      
      setIsTracking(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentGiven, config, handleScroll]);

  // Track session start once when tracking is initialized
  useEffect(() => {
    if (isTracking && !sessionStartTracked && consentGiven && config?.analytics_enabled) {
      // Check if we've already tracked session start for this session ID
      const sessionKey = `session_tracked_${sessionId}`;
      const alreadyTracked = sessionStorage.getItem(sessionKey);
      
      if (!alreadyTracked) {
        setSessionStartTracked(true);
        sessionStorage.setItem(sessionKey, 'true');
        trackEvent('session_start', {
          session_id: sessionId,
          user_type: user ? user.role : 'anonymous'
        });
      }
    }
  }, [isTracking, sessionStartTracked, consentGiven, config?.analytics_enabled, sessionId, user?.role]);

  // Process event queue before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Track session end
      trackEvent('session_end', {
        session_duration: Math.round((Date.now() - pageStartTime) / 1000),
        final_scroll_depth: maxScrollDepth
      });
      
      // Process remaining events
      processEventQueue();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [trackEvent, pageStartTime, maxScrollDepth, processEventQueue]);

  const value = {
    sessionId,
    clientId,
    isTracking,
    scrollDepth,
    maxScrollDepth,
    
    // Core tracking
    trackEvent,
    trackPageView,
    
    // E-commerce tracking
    trackProductView,
    trackCategoryView,
    trackProductListView,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewCart,
    trackBeginCheckout,
    trackAddPaymentInfo,
    trackPurchase,
    trackSearch,
    trackWishlistAdd,
    
    // Engagement tracking
    trackSocialShare,
    trackFormInteraction,
    
    // Utility
    processEventQueue
  };

  return (
    <EnhancedAnalyticsContext.Provider value={value}>
      {children}
    </EnhancedAnalyticsContext.Provider>
  );
};


